import styled from 'styled-components/macro';
import * as React from 'react';
import { ApiOutlined, BookOutlined, SettingOutlined, FileDoneOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { Button, Col, Dropdown, Menu, Radio, RadioChangeEvent, Row, Tooltip } from 'antd';
import { useAppConfig } from '../../useAppConfig';
import { AutoTooltip } from '../../entity/shared/components/common/AutoTooltip';
import { HOME_PAGE_INGESTION_ID } from '../../onboarding/config/HomePageOnboardingConfig';
import { useUpdateEducationStepIdsAllowlist } from '../../onboarding/useUpdateEducationStepIdsAllowlist';
import { useUserContext } from '../../context/useUserContext';
import DomainIcon from '../../domain/DomainIcon';
import { ReactComponent as IconAnalytics } from '../../../images/icon-data-analytics.svg';
import { ReactComponent as IconDataGovern } from '../../../images/icon-govern.svg';
import { ReactComponent as IconArrowDown } from '../../../images/keyboard_arrow_down.svg';
import { useTranslation } from '../../../i18n/useTranslation';
import { ReactComponent as IconHelpLink } from '../../../images/icon-help-outline.svg';
import { usePageType } from '../../search/context/BreadcrumbContext';
import { pageTypeMap } from '../pageTypeConstants';
import { postMessageAction, postMessageService } from '../postMessageService';
import { getHelpCenterUrlFromPathname } from '../utils/helpCenterUrlUtils';

export enum IngestionTabType {
    Sources = 'Sources',
    Secrets = 'Secrets',
}

const LinksWrapper = styled(Row)<{ areLinksHidden?: boolean }>`
    opacity: 1;
    transition: opacity 0.5s;

    ${(props) =>
        props.areLinksHidden &&
        `
        opacity: 0;
        width: 0;
    `}
`;

const MenuItem = styled(Menu.Item)`
    font-size: 14px;
    font-weight: bold;
    max-width: 240px;
`;

const NavTitleContainer = styled.span`
    gap: 8px;
    display: flex;
    align-items: center;
`;

const NavLink = styled(Link)`
    &&:after {
        content: none;
    }
`;

// const CustomSeparator = styled(Divider)`
//     height: 32px;
// `;

interface Props {
    areLinksHidden?: boolean;
    ingestionTabType?: IngestionTabType;
    showAnalyticsButton?: boolean;
    handleIngestionModeChange?: (e: RadioChangeEvent) => void;
}

export function HeaderLinks(props: Props) {
    const { areLinksHidden, showAnalyticsButton } = props;

    const me = useUserContext();
    const { t } = useTranslation();
    const { config } = useAppConfig();
    const pageType = usePageType();

    const isAnalyticsEnabled = config?.analyticsConfig.enabled;

    // const isPoliciesEnabled = config?.policiesConfig.enabled;
    // const isIdentityManagementEnabled = config?.identityManagementConfig.enabled;
    // const isIngestionEnabled = config?.managedIngestionConfig.enabled;

    // showAnalyticsButton is used to show/hide the analytics button in the header using props
    const showAnalytics =
        showAnalyticsButton !== undefined
            ? showAnalyticsButton
            : (isAnalyticsEnabled && me && me?.platformPrivileges?.viewAnalytics) || false;

    const showSettings = false; // true;
    const showIngestion = false;
    // isIngestionEnabled && me && me.platformPrivileges.manageIngestion && me.platformPrivileges.manageSecrets;
    // TODO: ask Joy/Asif for this type issue
    const showDomains = me?.platformPrivileges?.createDomains || me?.platformPrivileges?.manageDomains || false;

    useUpdateEducationStepIdsAllowlist(!!showIngestion, HOME_PAGE_INGESTION_ID);

    const handleHelpCenterClick = () => {
        const url = new URL(window.location.href);
        const { pathname } = url;
        const helpCenterUrl = getHelpCenterUrlFromPathname(pathname);
        postMessageService.sendToParent({
            action: postMessageAction.OPEN_HELP_CENTER,
            url: helpCenterUrl,
            pageType,
        });
    };

    return (
        <LinksWrapper className="f-gap-2" wrap={false} areLinksHidden={areLinksHidden}>
            {pageType === pageTypeMap.INGESTION && (
                <Col xs={24} lg={12} className="text-center">
                    <Radio.Group
                        className="f-btn-tabs mx-auto"
                        value={props.ingestionTabType}
                        onChange={props.handleIngestionModeChange}
                    >
                        <Radio.Button value={IngestionTabType.Sources}>{t('Sources')}</Radio.Button>
                        <Radio.Button value={IngestionTabType.Secrets}>{t('Secrets')}</Radio.Button>
                    </Radio.Group>
                </Col>
            )}
            {pageType !== pageTypeMap.INGESTION && (
                <>
                    {showAnalytics && (
                        <Col flex="none">
                            <Tooltip title={t('View data catalog usage analytics')}>
                                <Button
                                    type="text"
                                    aria-label={t('Analytics')}
                                    onClick={() => {
                                        window.location.href = '/analytics';
                                    }}
                                >
                                    <IconAnalytics aria-hidden="true" />
                                    <span>{t('Analytics')}</span>
                                </Button>
                            </Tooltip>
                        </Col>
                    )}
                    <Col flex="none">
                        <Dropdown
                            trigger={['click']}
                            overlay={
                                <Menu className="f-dropdown-medium">
                                    <MenuItem key="0">
                                        <NavLink to="/glossary">
                                            <NavTitleContainer aria-label={t('Glossary')}>
                                                <BookOutlined />
                                                <span>{t('Glossary')}</span>
                                            </NavTitleContainer>
                                            <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                {t('VIEW_MODIFY_DATA_DICTIONARY_DESC')}
                                            </AutoTooltip>
                                        </NavLink>
                                    </MenuItem>
                                    {showDomains && (
                                        <MenuItem key="1">
                                            <NavLink to="/domains">
                                                <NavTitleContainer aria-label={t('Domains')}>
                                                    <DomainIcon ariaLabel={t('Domains')} />
                                                    <span>{t('Domains')}</span>
                                                </NavTitleContainer>
                                                <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                    {t('MANAGE_RELATED_DATA_ASSETS_DESC')}
                                                </AutoTooltip>
                                            </NavLink>
                                        </MenuItem>
                                    )}
                                    <MenuItem key="2">
                                        <NavLink to="/browse/dataProduct">
                                            <NavTitleContainer aria-label={t('Data Products')}>
                                                <FileDoneOutlined />
                                                <span>{t('Data Products')}</span>
                                            </NavTitleContainer>
                                            <AutoTooltip className="f-text-content f-color-dark-black-s70">
                                                {t('ORGANIZE_MANAGE_DATA_ASSETS_DESC')}
                                            </AutoTooltip>
                                        </NavLink>
                                    </MenuItem>
                                </Menu>
                            }
                        >
                            <Button type="text" aria-label={t('Govern')}>
                                <IconDataGovern aria-hidden="true" /> {t('Govern')} <IconArrowDown aria-hidden="true" />
                            </Button>
                        </Dropdown>
                    </Col>
                    {showIngestion && (
                        <Col flex="none">
                            <NavLink to="/ingestion">
                                <Button id={HOME_PAGE_INGESTION_ID} type="text">
                                    <Tooltip title={t('CONNECT_CATALOG_DATA_SOURCES_DESC')}>
                                        <ApiOutlined aria-hidden="true" />
                                        <span>{t('Ingestion')}</span>
                                    </Tooltip>
                                </Button>
                            </NavLink>
                        </Col>
                    )}
                    {showSettings && (
                        <Col flex="none">
                            <NavLink to="/settings">
                                <Button type="text">
                                    <Tooltip title={t('MANAGE_CATALOG_SETTINGS_DESC')}>
                                        <SettingOutlined />
                                    </Tooltip>
                                </Button>
                            </NavLink>
                        </Col>
                    )}
                </>
            )}
            <Col flex={pageType === pageTypeMap.INGESTION ? 'auto' : 'none'} className="text-right">
                <Tooltip title={t('VIEW_CATALOG_HELP_CENTER_DESC')} placement="bottomRight">
                    <Button
                        type="text"
                        className="f-btn-action"
                        aria-label={t('Help Center')}
                        onClick={handleHelpCenterClick}
                        icon={<IconHelpLink aria-hidden="true" />}
                    />
                </Tooltip>
            </Col>
        </LinksWrapper>
    );
}
